import { useMemo } from "react"
import axios from "axios"
import { useQuery, UseQueryResult, UseQueryOptions } from "@tanstack/react-query"

import { useAddGlobalToast } from "@app/providers/GlobalToastProvider"
import { useDebounce } from "@app/hooks/useDebounce"
import { BulkPart } from "@app/services/types"
import { extractApiErrorMessage } from "@app/utils/extractApiErrorMessage"

export const QUERY_KEY_PREFIX = "BulkPartsData"
const ERROR_TITLE = "Error Loading Bulk Parts"
const ERROR_MESSAGE = "There was an error loading bulk parts. Contact support if the issue persists."

type ApiParams = {
	query?: string
	size?: number
	page?: number
}

export type FetchBulkPartsParams = ApiParams & {
	options?: Omit<UseQueryOptions<BulkPartsResponse>, "queryKey" | "queryFn" | "meta">
	minChars?: number
	disabled?: boolean
	debounceMs?: number
}

type BulkPartsResponse = {
	bulk: BulkPart[]
	page: number
	size: number
	total: number
}

export const fetchBulkPartsData = async (params?: ApiParams) => {
	return axios
		.get<BulkPartsResponse>(`skunkworks/bulk/search`, {
			params,
		})
		.then((response) => response.data)
}

export const useBulkPartsQuery = (params?: FetchBulkPartsParams): UseQueryResult<BulkPartsResponse, Error> => {
	const { query, size, page, minChars = 3, disabled = false, debounceMs = 300, options = {} } = params ?? {}
	const addToast = useAddGlobalToast()

	const trimmedQuery = (query ?? "").trim()
	const debouncedQuery = useDebounce(trimmedQuery, debounceMs)

	const defaultEnabled = !disabled && debouncedQuery.length >= minChars
	const enabled = options.enabled ?? defaultEnabled

	const queryKey = useMemo(
		() => [QUERY_KEY_PREFIX, { query: debouncedQuery, size, page, minChars }],
		[debouncedQuery, size, page, minChars]
	)

	return useQuery({
		queryKey,
		queryFn: () => fetchBulkPartsData({ query: debouncedQuery, size, page }),
		meta: {
			onError: (error: Error) =>
				addToast({
					message: `${ERROR_TITLE}: ${extractApiErrorMessage(error, ERROR_MESSAGE)}`,
					severity: "error",
				}),
		},
		...options,
		enabled,
	})
}

export default useBulkPartsQuery
